import firebase from "@firebase/app";
import "@firebase/firestore";
import "@firebase/auth";
import { fbConfig } from "../config.js";
import { removeInternalPropsAndFuncs } from "../helpers";

import { toJS } from "mobx";

class FirebaseStore {
  constructor() {
    //firebase.initializeApp(fbConfig);

    this.emailAuthProvider = firebase.auth.EmailAuthProvider;

    this.auth = firebase.auth();
    this.db = firebase.firestore();

    this.googleProvider = new firebase.auth.GoogleAuthProvider();
    this.facebookProvider = new firebase.auth.FacebookAuthProvider();
    this.twitterProvider = new firebase.auth.TwitterAuthProvider();
  }

  doCreateUserWithEmailAndPassword = (email, password) =>
    this.auth.createUserWithEmailAndPassword(email, password);

  doSignInWithEmailAndPassword = (email, password) =>
    this.auth.signInWithEmailAndPassword(email, password);

  doSignInWithGoogle = () => this.auth.signInWithPopup(this.googleProvider);

  doSignInWithFacebook = () => this.auth.signInWithPopup(this.facebookProvider);

  doSignInWithTwitter = () => this.auth.signInWithPopup(this.twitterProvider);

  doSignInAnonymously = () => this.auth.signInAnonymously();

  doSignOut = () => this.auth.signOut();

  doPasswordReset = email => this.auth.sendPasswordResetEmail(email);

  doSendEmailVerification = () =>
    this.auth.currentUser.sendEmailVerification({
      url: process.env.REACT_APP_CONFIRMATION_EMAIL_REDIRECT
    });

  doPasswordUpdate = password => this.auth.currentUser.updatePassword(password);

  onAuthUserListener = (next, fallback) =>
    this.auth.onAuthStateChanged(async authUser => {
      next(authUser);
    });

  // *** API ***

  locale = () => this.db.collection(`localeNew`);
  user = key => this.db.collection(`usersMeta`).doc(key);
  users = () => this.db.collection("usersMeta");
  surveys = () => this.db.collection("surveys");
  survey = key => this.db.collection("surveys").doc(key);
  groups = () => this.db.collection("groups");
  group = key => this.db.collection("groups").doc(key);

  getLocale = async () => await this.db.collection("surveys").get();

  getSurvey = async surveyKey => await this.db.collection("surveys").doc(surveyKey).get();

  getSurveyQuestions = async surveyKey =>
    await this.db.collection("surveys").doc(surveyKey).collection("questions").get();

  getSurveyQuestion = async (surveyKey, questionKey) =>
    await this.db
      .collection("surveys")
      .doc(surveyKey)
      .collection("questions")
      .doc(questionKey)
      .get();

  getUsersMeta = async params => {
    let refMeta = this.db.collection("usersMeta");
    if (params && params.groupKey) {
      refMeta = refMeta.where("groups", "array-contains", params.groupKey);
    }
    if (params && params.createdBy) {
      refMeta = refMeta.where("createdBy", "==", params.createdBy);
    }
    if (params && params.createdByEmail) {
      refMeta = refMeta.where("email", "==", params.createdByEmail);
    }
    if (params && params.uid) {
      refMeta = refMeta.doc(params.uid);
    }

    return refMeta.get();
  };

  getRespondents = async params => {
    let ref = this.db.collection("surveys").doc(params.surveyKey).collection("respondents");
    if (params.groupKey) {
      if (Array.isArray(params.groupKey)) {
        ref = ref.where("groupKey", "in", params.groupKey);
      } else {
        ref = ref.where("groupKey", "==", params.groupKey);
      }
    }
    if (params.dateStart) {
      ref = ref.where("createdOn", ">=", params.dateStart);
    }
    if (params.dateEnd) {
      ref = ref.where("createdOn", "<=", params.dateEnd);
    }
    if (params.createdBy) {
      ref = ref.where("createdBy", "==", params.createdBy);
    }
    return ref.get();
  };

  saveQuestion = async (surveyKey, questionKey, question) => {
    const ref = await this.survey(surveyKey).collection("questions");
    let doc = questionKey ? ref.doc(questionKey) : ref.doc();
    (await questionKey) ? doc.update(question) : doc.set(question);
    return doc;
  };

  saveSurvey = async (surveyKey, survey) => {
    let doc = surveyKey ? this.survey(surveyKey) : this.surveys().doc();
    (await surveyKey) ? doc.update(survey) : doc.set(survey);
    return doc;
  };

  saveGroup = async (groupKey, group) => {
    let doc = groupKey ? this.group(groupKey) : this.groups().doc();
    (await groupKey) ? doc.update(group) : doc.set(group);
    return doc;
  };

  incrementRespondentCount = async ({ surveyKey, groupKey, groupRespondentCount }) => {
    let doc = this.survey(surveyKey);
    await doc.update("respondentCount", firebase.firestore.FieldValue.increment(1));

    if (groupKey) {
      let docGroup = this.db.collection("groups").doc(groupKey);
      await docGroup.get().then(doc => {
        if (doc.exists) {
          let respondentCount = doc.data().respondentCount;
          if (!respondentCount || isNaN(respondentCount[surveyKey])) {
            respondentCount = { [surveyKey]: 0 };
          }
          respondentCount[surveyKey]++;
          console.log("incrementRespondentCount", respondentCount, groupRespondentCount);
          docGroup.update("respondentCount", respondentCount);
        } else {
          console.log("No such document!");
        }
      });
    }

    return doc;
  };

  batchSetQuestions = async (surveyKey, questions) => {
    let batch = this.db.batch();
    const questionsRef = this.survey(surveyKey).collection("questions");
    questions.forEach(question => {
      const ref = questionsRef.doc();
      let q = removeInternalPropsAndFuncs(toJS(question));
      q.surveyKey = surveyKey;
      batch.set(ref, q);
    });
    return await batch.commit();
  };

  batchUpdateQuestions = async (surveyKey, questions, fields = false) => {
    let batch = this.db.batch();
    const questionsRef = this.survey(surveyKey).collection("questions");

    questions.forEach(question => {
      const ref = questionsRef.doc(question._key);
      let q = removeInternalPropsAndFuncs(toJS(question));
      q.surveyKey = surveyKey;
      console.log("questionsRef :", q.order);
      batch.update(ref, "order", q.order);
    });
    return await batch.commit();
  };

  batchSaveLocales = async (locales, parentKey) => {
    let batch = this.db.batch();
    const localeRef = this.db.collection("localeNew");
    locales.forEach(locale => {
      const keyArr = locale.parentKey.split("_");
      let ref = null;
      if (keyArr.find(key => key === "new")) {
        //ie. "new_option1"
        if (keyArr.length > 1) {
          const index = keyArr.findIndex(key => key === "new");
          keyArr[index] = parentKey;
          locale.parentKey = keyArr.join("_");
        } else {
          locale.parentKey = parentKey;
        }
      }
      ref = localeRef.doc(locale.parentKey);
      batch.set(ref, removeInternalPropsAndFuncs(toJS(locale)));
    });
    return await batch.commit();
  };
}

export default FirebaseStore;
